/**
 * Font Extractor Utility
 * State'ten kullanılan font'ları extract eder ve Google Fonts'tan indirir
 */

/**
 * Font bilgisi objesi
 * @typedef {Object} FontInfo
 * @property {string} name - Font ismi
 * @property {Set<number>} weights - Kullanılan font-weight değerleri
 */

/**
 * Desteklenen Google Fonts listesi
 * Bu fontlar otomatik olarak Google Fonts'tan yüklenir (popup gösterilmez)
 */
export const SUPPORTED_GOOGLE_FONTS = [
  "Montserrat",
  "Lato",
  "Poppins",
  "Open Sans",
  "Raleway",
  "Playfair Display",
  "Merriweather",
  "Libre Baskerville",
  "Crimson Text",
  "Cormorant Garamond",
  "DM Sans",
  "Plus Jakarta Sans",
  "Manrope",
  "Outfit",
  "Sora",
  "Nunito",
  "Nunito Sans",
  "Karla",
  "Cabin",
  "Work Sans",
  "Fira Sans",
  "Source Sans 3",
  "Oswald",
  "Josefin Sans",
  "Barlow",
  "Barlow Condensed",
  "Exo 2",
  "Titillium Web",
  "Quicksand",
  "Ubuntu",
  "PT Sans",
  "Roboto",
  "Roboto Slab",
  "Rubik",
  "Jost",
  "Urbanist",
  "Be Vietnam Pro",
  "Mulish",
  "Lexend",
  "Lexend Deca",
  "Inter",
  "Arvo",
  "Bitter",
  "Vollkorn",
  "EB Garamond",
  "Spectral",
  "Instrument Sans",
  "Bricolage Grotesque",
  "Libre Franklin",
  "Alata",
  "Kalam",
];

/**
 * Lokal TTF dosyalarından yüklenen fontlar (Google Fonts'ta yok)
 * Bu fontlar fonts/ klasöründeki TTF dosyalarından yüklenir
 */
export const LOCAL_FONTS = ["Geist", "Switzer", "Zodiak", "Interphases Pro", "Stratum No1"];

export function isSupportedGoogleFont(fontName) {
  return SUPPORTED_GOOGLE_FONTS.includes(fontName);
}

export function isLocalFont(fontName) {
  return LOCAL_FONTS.includes(fontName);
}

/**
 * Font family isminin güvenli karakter setine (harf, rakam, boşluk, tire) uyup
 * uymadığını kontrol eder. Bu, extract edilen font isimleri export edilen
 * HTML/CSS'e ham olarak (<%- %>) yazıldığı için enjeksiyon (XSS) yüzeyini
 * kaynağında kapatır.
 * @param {string} fontName - Kontrol edilecek font ismi
 * @returns {boolean} - Güvenli ise true
 */
export function isSafeFontName(fontName) {
  return (
    typeof fontName === "string" &&
    fontName.length > 0 &&
    fontName.length <= 60 &&
    /^[\p{L}\p{N} -]+$/u.test(fontName)
  );
}

/**
 * Font'un sistemde yüklü olup olmadığını kontrol eder (Canvas API kullanarak)
 * @param {string} fontName - Kontrol edilecek font ismi
 * @returns {Promise<boolean>} - Font sistemde varsa true
 */
export function isFontAvailable(fontName) {
  return new Promise((resolve) => {
    if (!fontName || typeof fontName !== "string") {
      resolve(false);
      return;
    }

    if (typeof document === "undefined" || !document.createElement) {
      resolve(false);
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        resolve(false);
        return;
      }

      const testString = "mmmmmmmmmmlli";
      const testSize = "72px";
      const baselineFont = `${testSize} monospace`;
      const testFont = `${testSize} "${fontName}", monospace`;

      context.font = baselineFont;
      const baselineWidth = context.measureText(testString).width;

      context.font = testFont;
      const testWidth = context.measureText(testString).width;

      const isAvailable = Math.abs(baselineWidth - testWidth) > 1;
      setTimeout(() => {
        context.font = testFont;
        const finalWidth = context.measureText(testString).width;
        const finalCheck = Math.abs(baselineWidth - finalWidth) > 1;
        resolve(finalCheck);
      }, 100);
    } catch (error) {
      console.warn("[Font Extractor] Font kontrolü hatası:", error);
      resolve(false);
    }
  });
}

/**
 * Birden fazla font'un sistemde olup olmadığını kontrol eder
 * @param {Array<string>} fontNames - Kontrol edilecek font isimleri
 * @returns {Promise<Map<string, boolean>>} - Font ismi -> sistemde var mı mapping'i
 */
export async function checkFontsAvailability(fontNames) {
  const availabilityMap = new Map();

  if (!fontNames || fontNames.length === 0) {
    return availabilityMap;
  }

  const checkPromises = fontNames.map(async (fontName) => {
    const isAvailable = await isFontAvailable(fontName);
    availabilityMap.set(fontName, isAvailable);
    return { fontName, isAvailable };
  });

  await Promise.all(checkPromises);
  return availabilityMap;
}

function decodeStyleEntities(style) {
  return style
    .replace(/&quot;|&#0*34;|&#x0*22;/gi, '"')
    .replace(/&apos;|&#0*39;|&#x0*27;/gi, "'")
    .replace(/&amp;/gi, "&");
}

/**
 * State'ten tüm font-family ve font-weight değerlerini extract eder
 * @param {Object} state - IdealStudio state objesi
 * @returns {Map<string, Set<number>>} - Font ismi -> font-weight set'i mapping'i
 */
export function extractFontsFromState(state) {
  const fonts = new Map();

  const standardFonts = [
    "Arial",
    "Helvetica",
    "Times New Roman",
    "Times",
    "Courier New",
    "Courier",
    "Verdana",
    "Georgia",
    "Palatino",
    "Garamond",
    "Bookman",
    "Comic Sans MS",
    "Trebuchet MS",
    "Arial Black",
    "Impact",
    "Tahoma",
    "Lucida Sans Unicode",
    "Lucida Grande",
    "Geneva",
    "sans-serif",
    "serif",
    "monospace",
    "cursive",
  ];

  if (state && state.pluginToolbarsById) {
    Object.values(state.pluginToolbarsById).forEach((toolbar) => {
      if (toolbar.state && toolbar.state.__text) {
        const text = toolbar.state.__text;
        let decodedText = text;
        try {
          decodedText = decodeURIComponent(text);
        } catch (e) {
          decodedText = text;
        }

        const styleRegex = /style\s*=\s*(["'])((?:(?!\1).)*?)\1/gi;
        let styleMatch;

        while ((styleMatch = styleRegex.exec(decodedText)) !== null) {
          // Serialized HTML writes `font-family: "Poppins"` as `&quot;Poppins&quot;`;
          // undecoded, the `;` of the entity cuts the name to `&quot`.
          const styleContent = decodeStyleEntities(styleMatch[2]);

          let fontFamilyMatch = styleContent.match(/font-family\s*:\s*["']([^"']+)["']/i);
          let fontName = "";

          if (fontFamilyMatch && fontFamilyMatch[1]) {
            const rawFontName = fontFamilyMatch[1].trim();
            const fontParts = rawFontName.split(",");
            fontName = fontParts[0].trim().replace(/["']/g, "");
          } else {
            fontFamilyMatch = styleContent.match(/font-family\s*:\s*([^,;]+)/i);
            if (fontFamilyMatch && fontFamilyMatch[1]) {
              fontName = fontFamilyMatch[1].trim().replace(/["']/g, "");
            }
          }

          if (
            fontName &&
            isSafeFontName(fontName) &&
            !standardFonts.includes(fontName) &&
            fontName !== "inherit"
          ) {
            const fontWeightMatch = styleContent.match(/font-weight\s*:\s*(\d+)/i);
            let fontWeight = 400;

            if (fontWeightMatch) {
              const weightValue = parseInt(fontWeightMatch[1], 10);
              if (weightValue >= 100 && weightValue <= 900) {
                fontWeight = weightValue;
              }
            } else {
              const fontWeightTextMatch = styleContent.match(/font-weight\s*:\s*(\w+)/i);
              if (fontWeightTextMatch) {
                const weightText = fontWeightTextMatch[1].toLowerCase();
                if (weightText === "bold" || weightText === "700") {
                  fontWeight = 700;
                } else if (weightText === "normal" || weightText === "400") {
                  fontWeight = 400;
                } else if (weightText === "lighter" || weightText === "300") {
                  fontWeight = 300;
                } else if (weightText === "bolder" || weightText === "600") {
                  fontWeight = 600;
                }
              }
            }

            if (!fonts.has(fontName)) {
              fonts.set(fontName, new Set());
            }
            fonts.get(fontName).add(fontWeight);

            if (decodedText.match(/<b\b|<strong\b/i)) {
              fonts.get(fontName).add(700);
            }

            if (styleContent.match(/font-weight\s*:\s*bold/i)) {
              fonts.get(fontName).add(700);
            }
          }
        }
      }
    });
  }

  return fonts;
}

const DEFAULT_FAMILY_WEIGHTS = [400, 700];
// Values a font field can hold that are not family names.
const CSS_FAMILY_KEYWORDS = [
  "inherit",
  "initial",
  "unset",
  "revert",
  "default",
  "sans-serif",
  "serif",
  "monospace",
  "cursive",
  "system-ui",
];

// "Poppins, sans-serif" → "Poppins"
function familyName(stack) {
  if (!stack || typeof stack !== "string") return "";
  return stack.split(",")[0].replace(/["']/g, "").trim();
}

/**
 * Every family the training can render with: inline `font-family` in box HTML
 * plus the three places a family lives without appearing there — the box pill,
 * the scene default and the training default. A family named there carries no
 * weight, so it gets the two the editor's pill can produce.
 * @param {Object} state - IdealStudio state objesi
 * @returns {Map<string, Set<number>>}
 */
export function collectTrainingFonts(state) {
  const fonts = extractFontsFromState(state) || new Map();
  if (!state) return fonts;

  const addFamily = (stack) => {
    const name = familyName(stack);
    if (!name || !isSafeFontName(name) || CSS_FAMILY_KEYWORDS.includes(name.toLowerCase())) return;
    if (!fonts.has(name)) fonts.set(name, new Set());
    DEFAULT_FAMILY_WEIGHTS.forEach((w) => fonts.get(name).add(w));
  };

  Object.values(state.pluginToolbarsById || {}).forEach((tb) => {
    addFamily(tb && tb.state && tb.state.textFontFamily);
    // VectorShape: the text inside a shape (plugins/VectorShape).
    addFamily(tb && tb.state && tb.state.text && tb.state.text.font);
  });
  Object.values(state.viewToolbarsById || {}).forEach((vt) => {
    addFamily(vt && vt.fontFamily);
  });
  addFamily(state.globalConfig && state.globalConfig.fontFamily);

  return fonts;
}

/**
 * Google Fonts API'den font yüklemek için link tag'i oluşturur
 * @param {Map<string, Set<number>>} fontsMap - Font ismi -> font-weight set'i mapping'i
 * @returns {string} - Google Fonts link tag'i
 */
export function generateGoogleFontsLink(fontsMap) {
  if (!fontsMap || fontsMap.size === 0) {
    return "";
  }

  const fontParams = [];

  fontsMap.forEach((weights, fontName) => {
    if (!isSafeFontName(fontName)) {
      return;
    }
    // Lokal fontlar Google Fonts'ta yok; istenirse Google css2 API 400 döner ve
    // aynı istekteki geçerli Google fontlarını da düşürebilir. Bunlar statik
    // @font-face ile yükleniyor (editör index.html + visor index.ejs).
    if (isLocalFont(fontName)) {
      return;
    }
    if (fontName.includes(" ") && fontName.split(" ").length > 3) {
      return;
    }
    const fontNameParam = encodeURIComponent(fontName).replace(/%20/g, "+");

    const sortedWeights = Array.from(weights).sort((a, b) => a - b);
    const weightsParam = sortedWeights.join(";");
    fontParams.push(`family=${fontNameParam}:wght@${weightsParam}`);
  });

  if (fontParams.length === 0) {
    return "";
  }

  const fontFamilyParam = fontParams.join("&");
  const linkTag = `<link href="https://fonts.googleapis.com/css2?${fontFamilyParam}&display=swap" rel="stylesheet">`;

  return linkTag;
}

/**
 * Font'ları extract edip @font-face CSS'i oluşturur
 * @param {Map<string, Set<number>>} fontsMap - Font ismi -> font-weight set'i mapping'i
 * @param {Object} fontMap - Font ismi -> font dosya yolu mapping'i (opsiyonel)
 * @returns {string} - @font-face CSS string'i
 */
export function generateFontFaceCSS(fontsMap, fontMap = {}) {
  if (!fontsMap || fontsMap.size === 0) {
    return "";
  }

  const fontFaceRules = [];

  fontsMap.forEach((weights, fontName) => {
    if (!isSafeFontName(fontName)) {
      return;
    }
    // Lokal fontlar statik @font-face bloklarıyla (visor index.ejs) yükleniyor.
    // Buradan üretseydik `fonts/google/<isim>-<weight>.ttf` gibi var olmayan bir
    // yola işaret edip statik tanımı ezerdi (sonra gelen kural kazanır) ve font
    // yedeğe düşerdi. O yüzden lokal fontları burada atlıyoruz.
    if (isLocalFont(fontName)) {
      return;
    }
    // CSS string literal içine yazılıyor - tırnak/backslash kaçışı (savunma katmanı)
    const safeFontName = fontName.replace(/[\\"]/g, "\\$&");
    weights.forEach((weight) => {
      const subsets = fontMap[fontName] && fontMap[fontName][weight];
      if (Array.isArray(subsets)) {
        subsets.forEach(({ path, format, unicodeRange }) => {
          fontFaceRules.push(`@font-face {
  font-family: "${safeFontName}";
  src: url(${path}) format("${format}");
  font-weight: ${weight};
  font-style: normal;
  font-display: swap;${unicodeRange ? `\n  unicode-range: ${unicodeRange};` : ""}
}`);
        });
      } else if (fontMap[fontName]) {
        const fontPath =
          typeof fontMap[fontName] === "string"
            ? fontMap[fontName]
            : fontMap[fontName][weight] || fontMap[fontName]["default"];

        if (fontPath) {
          const extension = fontPath.match(/\.([^.?#]+)(\?|#|$)/)?.[1]?.toLowerCase();
          let format = "truetype";
          if (extension === "woff2") {
            format = "woff2";
          } else if (extension === "woff") {
            format = "woff";
          } else if (extension === "ttf" || extension === "otf") {
            format = "truetype";
          }

          fontFaceRules.push(`@font-face {
  font-family: "${safeFontName}";
  src: url(${fontPath}) format("${format}");
  font-weight: ${weight};
  font-style: normal;
}`);
        }
      } else {
        const fontBaseName = fontName.toLowerCase().replace(/\s+/g, "");
        const fontFileNameWoff2 = `${fontBaseName}-${weight}.woff2`;
        const fontFileNameTtf = `${fontBaseName}-${weight}.ttf`;
        fontFaceRules.push(`@font-face {
  font-family: "${safeFontName}";
  src: url(fonts/google/${fontFileNameWoff2}) format("woff2"),
       url(fonts/google/${fontFileNameTtf}) format("truetype");
  font-weight: ${weight};
  font-style: normal;
}`);
      }
    });
  });

  const css = fontFaceRules.join("\n\n");

  return css;
}

/**
 * Google Fonts CSS'inden font dosya URL'lerini extract eder
 * @param {string} googleFontsCSS - Google Fonts CSS string'i
 * @returns {Array<{fontFamily: string, fontWeight: number, url: string, fileName: string}>} - Font dosya bilgileri
 */
export function extractFontUrlsFromCSS(googleFontsCSS) {
  const fontFiles = [];

  if (!googleFontsCSS) {
    return fontFiles;
  }

  // Google splits each weight into unicode-range subsets (latin, latin-ext, …),
  // each preceded by a `/* subset */` comment. Every subset is kept as its own
  // file — Turkish ş/ğ/İ live in latin-ext, not latin.
  const fontFaceRegex = /(?:\/\*\s*([^*]+?)\s*\*\/\s*)?@font-face\s*\{([^}]+)\}/gi;
  let fontFaceMatch;

  while ((fontFaceMatch = fontFaceRegex.exec(googleFontsCSS)) !== null) {
    const subset = (fontFaceMatch[1] || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
    const fontFaceContent = fontFaceMatch[2];
    const rawRange = fontFaceContent.match(/unicode-range\s*:\s*([^;]+)/i)?.[1]?.trim() || "";
    // Written raw into the exported CSS — accept only a plain range list.
    const unicodeRange = /^[uU+0-9a-fA-F?, -]+$/.test(rawRange) ? rawRange : "";

    const fontFamilyMatch = fontFaceContent.match(/font-family\s*:\s*["']?([^"';]+)["']?/i);
    const fontWeightMatch = fontFaceContent.match(/font-weight\s*:\s*(\d+)/i);

    if (!fontFamilyMatch) {
      continue;
    }

    const fontFamily = fontFamilyMatch[1].trim();
    const fontWeight = fontWeightMatch ? parseInt(fontWeightMatch[1], 10) : 400;

    const srcMatch = fontFaceContent.match(/src\s*:\s*([^;]+)/i);
    if (!srcMatch) {
      continue;
    }

    const srcContent = srcMatch[1];

    let fontUrl = srcContent.match(/url\(["']?([^"')]+\.woff2[^"')]*)["']?\)/i)?.[1];
    let format = "woff2";

    if (!fontUrl) {
      fontUrl = srcContent.match(/url\(["']?([^"')]+\.woff[^"')]*)["']?\)/i)?.[1];
      format = "woff";
    }

    if (!fontUrl) {
      fontUrl = srcContent.match(/url\(["']?([^"')]+\.ttf[^"')]*)["']?\)/i)?.[1];
      format = "truetype";
    }

    if (!fontUrl) {
      fontUrl = srcContent.match(/url\(["']?([^"')]+)["']?\)/i)?.[1];
      format = "woff2";
    }

    if (fontUrl) {
      fontUrl = fontUrl.trim();

      const fontBaseName = fontFamily.toLowerCase().replace(/\s+/g, "");
      let urlExtension = fontUrl.match(/\.([^.?#]+)(\?|#|$)/)?.[1];
      if (!urlExtension) {
        urlExtension = format === "truetype" ? "ttf" : format;
      }
      const fileName = `${fontBaseName}-${fontWeight}${subset ? `-${subset}` : ""}.${urlExtension}`;

      fontFiles.push({
        fontFamily,
        fontWeight,
        url: fontUrl,
        fileName: fileName,
        format: format,
        unicodeRange,
      });
    }
  }

  return fontFiles;
}

/**
 * Font URL'sinin güvenli olup olmadığını kontrol eder
 * @param {string} url - Kontrol edilecek URL
 * @returns {boolean} - URL güvenli ise true
 */
function isValidFontUrl(url) {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const urlObj = new URL(url);

    const ALLOWED_DOMAINS = ["fonts.googleapis.com", "fonts.gstatic.com"];

    const isValidDomain = ALLOWED_DOMAINS.some(
      (domain) => urlObj.hostname === domain || urlObj.hostname.endsWith("." + domain)
    );

    const isHttps = urlObj.protocol === "https:";

    return isValidDomain && isHttps;
  } catch (error) {
    console.warn("[Font Extractor] Geçersiz URL formatı:", url, error);
    return false;
  }
}

/**
 * Font dosya uzantısının geçerli olup olmadığını kontrol eder
 * @param {string} fileName - Kontrol edilecek dosya adı
 * @returns {boolean} - Uzantı geçerli ise true
 */
function isValidFontExtension(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return false;
  }

  const VALID_EXTENSIONS = [".woff2", ".woff", ".ttf", ".otf"];
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf("."));
  return VALID_EXTENSIONS.includes(ext);
}

/**
 * Google Fonts CSS'ini fetch edip font dosya URL'lerini döndürür
 * @param {Map<string, Set<number>>} fontsMap - Font ismi -> font-weight set'i mapping'i
 * @returns {Promise<Array<{fontFamily: string, fontWeight: number, url: string, fileName: string}>>} - Font dosya bilgileri
 */
export async function fetchGoogleFontsFiles(fontsMap) {
  if (!fontsMap || fontsMap.size === 0) {
    return [];
  }

  const googleFontsLink = generateGoogleFontsLink(fontsMap);

  if (!googleFontsLink) {
    return [];
  }

  const urlMatch = googleFontsLink.match(/href=["']([^"']+)["']/);
  if (!urlMatch) {
    return [];
  }

  const googleFontsUrl = urlMatch[1];

  if (!isValidFontUrl(googleFontsUrl)) {
    console.warn("[Font Extractor] Güvenlik: Geçersiz Google Fonts URL:", googleFontsUrl);
    return [];
  }

  try {
    const response = await fetch(googleFontsUrl);
    if (!response.ok) {
      console.warn("[Font Extractor] Google Fonts CSS fetch edilemedi:", response.status);
      return [];
    }

    const cssText = await response.text();

    const fontFiles = extractFontUrlsFromCSS(cssText);

    const validFontFiles = fontFiles.filter((file) => {
      const isUrlValid = isValidFontUrl(file.url);
      const isExtensionValid = isValidFontExtension(file.fileName);

      if (!isUrlValid) {
        console.warn("[Font Extractor] Güvenlik: Geçersiz font URL filtrelendi:", file.url);
      }
      if (!isExtensionValid) {
        console.warn(
          "[Font Extractor] Güvenlik: Geçersiz font uzantısı filtrelendi:",
          file.fileName
        );
      }

      return isUrlValid && isExtensionValid;
    });

    return validFontFiles;
  } catch (error) {
    console.error("[Font Extractor] Google Fonts fetch hatası:", error);
    return [];
  }
}

/**
 * Export fontMap'i: aile -> weight -> zip içindeki subset dosyaları.
 * generateFontFaceCSS her subset için ayrı (unicode-range'li) bir kural yazar.
 * @param {Array} fontFiles - fetchGoogleFontsFiles çıktısı
 * @param {string} dir - index.html'e göre font klasörü
 * @returns {Object}
 */
export function buildExportFontMap(fontFiles, dir = "fonts/google") {
  const fontMap = {};
  (fontFiles || []).forEach((file) => {
    const byWeight = (fontMap[file.fontFamily] = fontMap[file.fontFamily] || {});
    (byWeight[file.fontWeight] = byWeight[file.fontWeight] || []).push({
      path: `${dir}/${file.fileName}`,
      format: file.format || "woff2",
      unicodeRange: file.unicodeRange || "",
    });
  });
  return fontMap;
}

/**
 * Font dosyalarını indirip fontMap oluşturur
 * @param {Map<string, Set<number>>} fontsMap - Font ismi -> font-weight set'i mapping'i
 * @returns {Promise<Object>} - Font ismi -> font dosya yolu mapping'i
 */
export async function downloadFontsToMap(fontsMap) {
  const fontMap = {};

  if (!fontsMap || fontsMap.size === 0) {
    return fontMap;
  }

  const fontFiles = await fetchGoogleFontsFiles(fontsMap);

  fontFiles.forEach((file) => {
    if (!fontMap[file.fontFamily]) {
      fontMap[file.fontFamily] = {};
    }

    const fontPath = `fonts/${file.fileName}`;
    fontMap[file.fontFamily][file.fontWeight] = fontPath;
  });

  return fontMap;
}
